// Quick Estimate 3 — concurrency limiter.
//
// Caps how many evaluateImage requests run in parallel so we don't
// crush a service-bay LTE connection (or trip the BE's per-IP rate
// limit). Implementation is a plain reference-counted gate around
// any thunked async task; no third-party dep.
//
// Usage:
//   const limiter = createLimiter(3)
//   const result = await limiter.run(() => agentsApi.evaluateImage(file))
// limiter.pending() / limiter.active() expose counts the UI uses to
// show "AI tagging 3 of 5…" hints.

export interface Limiter {
  /** Run a thunk under the gate; resolves with the thunk's value. */
  run<T>(task: () => Promise<T>): Promise<T>
  /** Currently executing — does not include queued. */
  active: () => number
  /** Queued but not yet started. */
  pending: () => number
  /** Total = active + pending. Convenience for UI. */
  total: () => number
}

export function createLimiter(maxConcurrent: number): Limiter {
  let active = 0
  const queue: Array<() => void> = []

  function next() {
    if (active >= maxConcurrent) return
    const release = queue.shift()
    if (release) release()
  }

  return {
    run<T>(task: () => Promise<T>): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        const release = () => {
          active++
          task().then(
            (v) => {
              active--
              resolve(v)
              next()
            },
            (err) => {
              active--
              reject(err)
              next()
            },
          )
        }
        if (active < maxConcurrent) release()
        else queue.push(release)
      })
    },
    active: () => active,
    pending: () => queue.length,
    total: () => active + queue.length,
  }
}
